import { BreakoutButton } from 'components/design-system/BreakoutButton'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { Dialog } from 'components/dialogs/Dialog'
import { DialogCloseButton } from 'components/dialogs/DialogCloseButton'
import { DoubleCheckIcon } from 'components/icons/DoubleCheck'
import { useRepository } from 'hooks/auth'
import { useDialogs } from 'hooks/dialogs'
import { useTranslationTyped } from 'i18n/i18n'
import { observer } from 'mobx-react-lite'
import { useCallback, useState, useId } from 'react'
import { Trans } from 'react-i18next'

export const ResyncGradesDialog = observer(function ResyncGradesDialog({
  sectionId,
  assignmentId,
  roomId,
  userId,
  roomName,
  userName,
}: {
  sectionId: string
  assignmentId: string
  roomId?: string
  userId?: string
  roomName?: string
  userName?: string
}) {
  const { tt } = useTranslationTyped()
  const { popDialog } = useDialogs()
  const headingId = useId()
  const repository = useRepository()

  const [phase, setPhase] = useState<'form' | 'started'>('form')
  const [syncType, setSyncType] = useState<'missing' | 'all' | null>(null)

  const runResync = useCallback(async () => {
    if (!sectionId || !syncType) return

    const user = repository.breakoutUser
    if (!user) return

    // Use the cubit to create the job - this will trigger the cloud function
    await user.gradeSyncJobs.createJob({
      sectionId,
      assignmentId,
      roomStateId: roomId,
      targetUserId: userId,
      forceResend: syncType === 'all',
    })

    setPhase('started')
  }, [sectionId, syncType, repository, assignmentId, roomId, userId])

  return (
    <Dialog
      size="sm"
      ignoreBackdropClick
      innerClassName="h-full flex flex-col items-center justify-between"
      aria-labelledby={headingId}
    >
      <DialogCloseButton />
      <div className="flex h-full w-full flex-1 flex-col items-center justify-center">
        {phase === 'form' && (
          <div className="flex flex-col items-center gap-5 text-center">
            <div className="flex flex-col gap-3">
              <h1 id={headingId} className="text-headline-large">
                {tt.instructor_assignment.resync_grades_dialog.title()}
              </h1>
              <h2 className="text-body-medium">
                {tt.instructor_assignment.resync_grades_dialog.subtitle()}
              </h2>
              {roomName && (
                <div className="text-body-medium">
                  {tt.instructor_assignment.resync_grades_dialog.for_room({
                    roomName: roomName,
                  })}
                </div>
              )}
              {userName && (
                <div className="text-body-medium">
                  {tt.instructor_assignment.resync_grades_dialog.for_user({
                    userName: userName,
                  })}
                </div>
              )}
            </div>
            <BreakoutRadioGroup
              value={syncType ?? ''}
              onValueChange={(val) =>
                setSyncType(val as 'missing' | 'all' | null)
              }
              className="flex flex-col gap-2"
              aria-labelledby={headingId}
            >
              <div
                className="flex cursor-pointer flex-row gap-2 rounded-2xl border-2 border-outline-light px-4 py-4.5 hover:border-outline-medium"
                data-testid="resync-missing-option"
                onClick={() => setSyncType('missing')}
              >
                <BreakoutRadioButton
                  value="missing"
                  label={null}
                  variant="bare"
                  className="border-0"
                  data-testid="resync-missing-radio"
                  aria-labelledby={`${headingId}-sync-missing`}
                />
                <div className="flex flex-grow flex-col gap-1 text-left">
                  <h3
                    className="text-title-medium"
                    id={`${headingId}-sync-missing`}
                  >
                    {tt.instructor_assignment.resync_grades_dialog.sync_missing()}
                  </h3>
                  <h4 className="text-body-medium text-on-surface-var">
                    {tt.instructor_assignment.resync_grades_dialog.sync_missing_description()}
                  </h4>
                </div>
              </div>
              <div
                className="flex cursor-pointer flex-row gap-2 rounded-2xl border-2 border-outline-light px-4 py-4.5 hover:border-outline-medium"
                data-testid="resync-all-option"
                onClick={() => setSyncType('all')}
              >
                <BreakoutRadioButton
                  value="all"
                  label={null}
                  variant="bare"
                  className="border-0"
                  data-testid="resync-all-radio"
                  aria-labelledby={`${headingId}-sync-all`}
                />
                <div className="flex flex-grow flex-col gap-1 text-left">
                  <h3
                    className="text-title-medium"
                    id={`${headingId}-sync-all`}
                  >
                    {tt.instructor_assignment.resync_grades_dialog.sync_all()}
                  </h3>
                  <h4 className="text-body-medium text-on-surface-var">
                    <Trans
                      i18nKey={
                        'instructor_assignment.resync_grades_dialog.sync_all_description'
                      }
                    />
                  </h4>
                </div>
              </div>
            </BreakoutRadioGroup>
            <BreakoutButton
              data-testid="resync-confirm-button"
              tooltip={
                !syncType
                  ? tt.instructor_assignment.resync_grades_dialog.form_empty_button_tooltip()
                  : undefined
              }
              disabled={!syncType}
              onClick={() => {
                if (!syncType) return
                runResync()
              }}
              kind="accent"
              size="large"
              fullWidth
            >
              {tt.common.confirm()}
            </BreakoutButton>
          </div>
        )}
        {phase === 'started' && (
          <div className="flex flex-col items-center gap-3 text-center">
            <DoubleCheckIcon
              className="stroke-core-success"
              size={60}
              strokeWidth="1.25"
            />
            <div className="text-headline-large">
              {tt.instructor_class.resync_grades_started_title()}
            </div>
            <div className="px-2 text-body-medium">
              <Trans
                i18nKey={'instructor_class.resync_grades_started_description'}
                components={{
                  br: <br />,
                }}
              />
            </div>
            <BreakoutButton
              onClick={() => {
                popDialog()
              }}
              kind="accent"
              size="large"
              fullWidth
            >
              {tt.common.close()}
            </BreakoutButton>
          </div>
        )}
      </div>
    </Dialog>
  )
})
